import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from 'react';
import Decimal from 'decimal.js';
import { Column, Divider, TextButton } from 'seagull';
import { PlusCircle } from 'seagull/icons';
import { getTestIds } from 'seagull/utils/TestIds';
import { useLocaleKeys } from '~/i18n';
import { BillingStageScheduleType } from '~/models';
import { AutomaticStageRow } from './AutomaticStageRow';
import { withFinalStageComputed } from './finalStage';
import { defaultScheduleForType } from './scheduleForType';
import { computeTotalSumExceedsRate, getStageHelperText } from './stageValidation';
export const GetAutomaticBillingStagesTID = (parentTestId)=>getTestIds({
        parentTestId
    });
const MAX_STAGES = 6;
export const AutomaticBillingStages = (param)=>{
    let { testId, stages, context, valueType, totalRate, disabled, onStagesChange, onValueTypeChange } = param;
    const { AutomaticBillingStagesTestIds } = useMemo(()=>GetAutomaticBillingStagesTID(testId), [
        testId
    ]);
    const { t: stagesTexts } = useLocaleKeys('billing.stages');
    const computedStages = useMemo(()=>withFinalStageComputed({
            stages,
            valueType,
            totalRate
        }), [
        stages,
        valueType,
        totalRate
    ]);
    const totalSumExceedsRate = useMemo(()=>computeTotalSumExceedsRate(computedStages, valueType, totalRate), [
        computedStages,
        valueType,
        totalRate
    ]);
    const pushStages = (nextStages)=>{
        onStagesChange(withFinalStageComputed({
            stages: nextStages,
            valueType,
            totalRate
        }));
    };
    const handleStageChange = (index, next)=>{
        pushStages(computedStages.map((stage, i)=>i === index ? next : stage));
    };
    const handleRemove = (index)=>{
        pushStages(computedStages.filter((_, i)=>i !== index));
    };
    const handleAdd = ()=>{
        var _ref;
        var _computedStages_;
        const nextStageNumber = ((_ref = (_computedStages_ = computedStages[computedStages.length - 1]) === null || _computedStages_ === void 0 ? void 0 : _computedStages_.stageNumber) !== null && _ref !== void 0 ? _ref : 0) + 1;
        const newStage = {
            stageNumber: nextStageNumber,
            value: new Decimal(0),
            schedule: defaultScheduleForType(BillingStageScheduleType.Manual)
        };
        pushStages([
            ...computedStages,
            newStage
        ]);
    };
    return /*#__PURE__*/ _jsxs(Column, {
        gap: 16,
        style: {
            marginTop: 16
        },
        testId: AutomaticBillingStagesTestIds.Root(),
        children: [
            computedStages.map((stage, index)=>{
                const isLast = index === computedStages.length - 1;
                const helperText = getStageHelperText({
                    stage,
                    isLast,
                    valueType,
                    totalRate,
                    totalSumExceedsRate,
                    texts: stagesTexts
                });
                return /*#__PURE__*/ _jsxs(Fragment, {
                    children: [
                        index > 0 && /*#__PURE__*/ _jsx(Divider, {
                            margin: 0
                        }),
                        /*#__PURE__*/ _jsx(AutomaticStageRow, {
                            testId: AutomaticBillingStagesTestIds.Row(index),
                            stage: stage,
                            stageNumber: stage.stageNumber,
                            context: context,
                            isFirst: index === 0,
                            isLast: isLast,
                            canRemove: computedStages.length > 2 && index !== 0 && !isLast,
                            valueType: valueType,
                            totalRate: totalRate,
                            helperText: helperText,
                            disabled: disabled,
                            onChange: (next)=>handleStageChange(index, next),
                            onValueTypeChange: onValueTypeChange,
                            onRemove: ()=>handleRemove(index)
                        })
                    ]
                }, stage.stageNumber);
            }),
            /*#__PURE__*/ _jsx(TextButton, {
                bi: {
                    name: 'add_stage'
                },
                skin: "primary",
                weight: "normal",
                underline: false,
                prefixIcon: /*#__PURE__*/ _jsx(PlusCircle, {
                    size: 20
                }),
                disabled: disabled || computedStages.length >= MAX_STAGES,
                onClick: handleAdd,
                testId: AutomaticBillingStagesTestIds.AddStage(),
                style: {
                    alignSelf: 'flex-start',
                    width: 'fit-content'
                },
                children: stagesTexts.schedule.ctas.addStage()
            })
        ]
    });
};
